import { X } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import { cn } from '@/shared/utils/cn/cn.util';

import { KeyValueKeyCell } from '../KeyValueKeyCell/KeyValueKeyCell';
import { KeyValueValueCell } from '../KeyValueValueCell/KeyValueValueCell';
import {
  KEY_VALUE_ACTIONS_CLASSES,
  KEY_VALUE_ROW_CLASSES,
  KEY_VALUE_ROW_COLUMN_CLASSES,
  KEY_VALUE_ROW_ERROR_CLASSES,
  KEY_VALUE_ROW_WARNING_CLASSES,
} from '../structured.tokens';

import { type KeyValueRowProps } from './KeyValueRow.types';

/**
 * One entry: its key, its value, its problems and its remove button.
 *
 * The row is a grid that **stacks below `sm`**. Two flex halves at 320px gave a
 * key box and a value box about nine characters wide each; stacked, both get the
 * full width and each carries its own visible label (`sm:sr-only`, so the label
 * survives for assistive tech once the columns split and the header-less layout
 * reads correctly at every width).
 *
 * Presentation only (U5).
 */
export function KeyValueRow({
  row,
  index,
  size,
  columns,
  options,
  keyPlaceholder,
  valuePlaceholder,
  valueDir,
  isDisabled,
  isReadOnly,
  onKeyChange,
  onTextChange,
  onBooleanChange,
  onAdoptKind,
  onRemove,
  onBlur,
  registerControl,
}: KeyValueRowProps) {
  const { t } = useTranslation('uiKit');
  const messageId = useId();
  const issuesId = useId();

  const describedBy =
    [row.message === null ? null : messageId, row.issues.length === 0 ? null : issuesId]
      .filter((id) => id !== null)
      .join(' ') || undefined;

  return (
    <li className="flex flex-col gap-xs">
      <div className={cn(KEY_VALUE_ROW_CLASSES, KEY_VALUE_ROW_COLUMN_CLASSES[columns])}>
        <KeyValueKeyCell
          row={row}
          index={index}
          size={size}
          placeholder={keyPlaceholder}
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onBlur={onBlur}
          onKeyChange={onKeyChange}
        />

        <KeyValueValueCell
          row={row}
          index={index}
          size={size}
          options={options}
          placeholder={valuePlaceholder}
          valueDir={valueDir}
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          describedById={describedBy}
          registerControl={registerControl}
          onBlur={onBlur}
          onTextChange={onTextChange}
          onBooleanChange={onBooleanChange}
          onAdoptKind={onAdoptKind}
        />

        <div className={KEY_VALUE_ACTIONS_CLASSES}>
          {/* A required key, a disabled field and a read-only field each have
              NOTHING for a refusal to explain, so the button is not drawn at
              all rather than drawn and then refused. */}
          {row.isRemovable && !isDisabled && !isReadOnly ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              // Named by the entry, so a screen reader hears which one goes
              // rather than N identical buttons.
              aria-label={t('keyValue.removeRow', {
                key: row.entry?.label ?? (row.key.trim() === '' ? String(index + 1) : row.key),
              })}
              onClick={onRemove}
            >
              <X aria-hidden className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      {row.message === null ? null : (
        <p
          id={messageId}
          role={row.message.severity === 'error' ? 'alert' : undefined}
          className={
            row.message.severity === 'error'
              ? KEY_VALUE_ROW_ERROR_CLASSES
              : KEY_VALUE_ROW_WARNING_CLASSES
          }
        >
          {row.message.text}
        </p>
      )}

      {row.issues.map((issue) => (
        <p
          key={issue.message}
          id={issuesId}
          className={
            issue.severity === 'warning'
              ? KEY_VALUE_ROW_WARNING_CLASSES
              : KEY_VALUE_ROW_ERROR_CLASSES
          }
        >
          {issue.message}
        </p>
      ))}
    </li>
  );
}
